import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { img } from '@/lib/images';
import CheckInOutNote from '@/components/feature/CheckInOutNote';
import { money } from '@/lib/currency';
import { useExchangeRate } from '@/hooks/usePrices';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const BALI_CHAKRA_LOGO = img('about/logo-bali-chakra-tour_01.webp');

interface ExcursionVoucherData {
  id: string;
  booking_no?: string | null;
  lang?: string;
  type: string;
  type_label: string;
  title: string;
  arrival_display: string;
  date_display: string;
  start_display: string;
  end_display: string;
  adults: number;
  children: number;
  hotel: string | null;
  operator: string | null;
  nationality: string | null;
  guidelang: string | null;
  guest_name: string;
  email: string;
  phone: string | null;
  total_price: number | null;
  subtotal: number | null;
  discount: number | null;
  status: string;
  excursions: string | null;
  transfers: string | null;
  arrival_flight?: string | null;
  departure_flight?: string | null;
  arrival_pickup?: string | null;
  departure_pickup?: string | null;
  departure_destination?: string | null;
  error?: string;
}

const STATUS_LABELS: Record<string, Record<string, string>> = {
  pending: { de: 'Bestätigt', en: 'Confirmed' },
  confirmed: { de: 'Bestätigt', en: 'Confirmed' },
  cancelled: { de: 'Storniert', en: 'Cancelled' },
};

const PRINT_CSS = `
  @page { size: A4; margin: 3cm 0 0 0; }
  @media print {
    body { margin: 0 !important; padding: 0 !important; }
    .voucher-page { padding: 0 !important; min-height: 0 !important; background: #fff !important; }
    .voucher-sheet { font-size: 11px; }
    .vh-header { padding: 10px 28px !important; }
    .vh-header-gap { gap: 6px !important; }
    .vh-logo { width: 200px !important; }
    .vh-header h1 { font-size: 20px !important; }
    .vh-title-block { padding: 10px 3cm 4px !important; }
    .vh-title-block h2 { font-size: 17px !important; }
    .vh-body { padding: 0 3cm 8px !important; }
    .vh-body > p { margin-bottom: 6px !important; }
    .vh-guest { padding: 7px 12px !important; margin-bottom: 6px !important; }
    .vh-table td { padding-top: 3px !important; padding-bottom: 3px !important; font-size: 11px !important; }
    .vh-footer { padding: 8px 3cm !important; font-size: 10px !important; line-height: 1.4 !important; }
    .vh-footer p { margin-bottom: 4px !important; }
  }
`;

export default function ExcursionVoucherPage() {
  useExchangeRate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || searchParams.get('excursion');
  const [data, setData] = useState<ExcursionVoucherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Ungültiger Bestätigungs-Link: Keine Buchungs-ID angegeben.');
      setLoading(false);
      return;
    }

    const fetchVoucher = async () => {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/excursion-voucher?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
          },
        );
        const result = (await response.json()) as ExcursionVoucherData;

        if (!response.ok || result.error) {
          throw new Error(
            result.error ||
              `Fehler beim Laden der Bestätigung (Status ${response.status})`,
          );
        }

        setData(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Die Bestätigung konnte nicht geladen werden.',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVoucher();
  }, [id]);

  useEffect(() => {
    document.title = 'Ausflugs-Bestätigung – Bali Chakra Tour';
    return () => {
      document.title = 'Bali VW Tour';
    };
  }, []);

  const isEn = data?.lang === 'en';

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center text-foreground-500">
        {isEn ? 'Loading confirmation...' : 'Bestätigung wird geladen...'}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
        <div className="bg-background-50 p-8 rounded-lg border border-background-200 max-w-md text-center">
          <h2 className="text-xl font-semibold text-foreground-800 mb-2">
            {isEn ? 'Confirmation unavailable' : 'Bestätigung nicht verfügbar'}
          </h2>
          <p className="text-foreground-500">
            {error || (isEn ? 'No data available.' : 'Keine Daten vorhanden.')}
          </p>
        </div>
      </div>
    );
  }

  const statusLabel =
    (STATUS_LABELS[data.status] && STATUS_LABELS[data.status][isEn ? 'en' : 'de']) ||
    data.status;
  const isMultiday = data.type === 'multiday';
  const isBaustein = data.type === 'baustein';
  const isPeriod = isMultiday || isBaustein;

  const dateLabel = isPeriod
    ? (isEn ? 'Period' : 'Zeitraum')
    : (isEn ? 'Date' : 'Datum');
  const dateValue = isPeriod
    ? `${data.start_display} – ${data.end_display}`
    : data.date_display;

  return (
    <div className="voucher-page min-h-screen bg-background-100 py-6 px-4">
      <div className="max-w-3xl mx-auto text-right mb-4 print:hidden">
        <button
          type="button"
          onClick={() => window.print()}
          className="bg-blue-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-semibold hover:bg-blue-600 transition-colors whitespace-nowrap"
        >
          {isEn ? 'Print / save as PDF' : 'Drucken / als PDF speichern'}
        </button>
      </div>

      <style>{PRINT_CSS}</style>
      <div className="voucher-sheet max-w-3xl mx-auto bg-background-50 rounded-lg overflow-hidden border border-blue-100 print:border-none print:rounded-none print:mx-0 print:max-w-full">
        {/* Header */}
        <div className="vh-header bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white px-7 py-8 text-center">
          <div className="vh-header-gap flex flex-col items-center gap-4">
            <div className="bg-blue-50 rounded-xl px-4 py-3">
              <img
                src={BALI_CHAKRA_LOGO}
                alt="Bali Chakra Tours & Travel Logo"
                className="vh-logo w-80 h-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold tracking-wide font-heading leading-tight">
                {isEn ? 'Booking Confirmation' : 'Buchungs-Bestätigung'}
              </h1>
              <p className="mt-3 inline-block bg-white/15 border border-white/30 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap">
                {isEn ? 'Booking no.' : 'Buchungs-Nr.'}: {data.booking_no || data.id}
              </p>
            </div>
          </div>
        </div>

        {/* Booking title */}
        <div className="vh-title-block px-8 pt-8 pb-4 bg-gradient-to-b from-blue-50 to-background-50">
          <p className="text-xs font-semibold tracking-widest uppercase text-blue-700 mb-1.5">
            {isEn ? 'Your booking' : 'Ihre Buchung'}
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold text-blue-900 font-heading leading-tight">
            {data.title}
          </h2>
          <span className="inline-block bg-blue-100 text-blue-900 border border-blue-200 px-3 py-1 rounded-full text-xs font-semibold mt-3 whitespace-nowrap">
            {statusLabel}
          </span>
        </div>

        {/* Body */}
        <div className="vh-body px-8 pb-8">
          <p className="text-sm text-foreground-500 mb-6">
            {isEn
              ? 'Thank you for your booking! Please bring this confirmation at pickup.'
              : 'Vielen Dank für Ihre Buchung! Bitte bringen Sie diese Bestätigung bei der Abholung mit.'}
          </p>

          <div className="vh-guest rounded p-3.5 mb-6 bg-blue-50 border-l-4 border-blue-500">
            <p className="text-base font-semibold text-foreground-800">
              {data.guest_name}
            </p>
            <p className="text-sm text-foreground-600">
              {data.email}
              {data.phone ? ` · ${data.phone}` : ''}
            </p>
          </div>

          <table className="vh-table w-full text-sm border-collapse">
            <tbody>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 w-2/5">
                  {isEn ? 'Type of booking' : 'Art der Buchung'}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.type_label}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isBaustein
                    ? (isEn ? 'Villa' : 'Villa')
                    : isMultiday
                    ? (isEn ? 'Round trip' : 'Rundreise')
                    : (isEn ? 'Activity / Tour' : 'Aktivität / Tour')}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.title}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {dateLabel}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {dateValue}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                  {isBaustein
                    ? (isEn ? 'Guests' : 'Personen')
                    : (isEn ? 'Adults' : 'Erwachsene')}
                </td>
                <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                  {data.adults}
                </td>
              </tr>
              {isBaustein && data.excursions && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Additional programs' : 'Zusatzprogramme'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.excursions.split(', ').map((item, i) => (
                      <div key={i} className="mb-1 last:mb-0">{item}</div>
                    ))}
                  </td>
                </tr>
              )}
              {isBaustein && data.transfers && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Transfers' : 'Transfers'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.transfers.split('; ').map((item, i) => (
                      <div key={i} className="mb-1 last:mb-0">{item}</div>
                    ))}
                  </td>
                </tr>
              )}
              {data.arrival_flight && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Arrival flight' : 'Flug Ankunft'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.arrival_flight}
                  </td>
                </tr>
              )}
              {data.arrival_pickup && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Pickup (arrival)' : 'Abholort Ankunft'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.arrival_pickup}
                  </td>
                </tr>
              )}
              {data.departure_flight && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Departure flight' : 'Flug Abreise'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.departure_flight}
                  </td>
                </tr>
              )}
              {data.departure_destination && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500 align-top">
                    {isEn ? 'Departure to' : 'Abreise zum'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.departure_destination}
                  </td>
                </tr>
              )}
              {!isBaustein && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {isEn ? 'Children' : 'Kinder'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.children}
                  </td>
                </tr>
              )}
              {data.hotel && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {data.type === 'adventure'
                      ? (isEn ? 'Hotel / Pickup' : 'Hotel / Abholung')
                      : (isEn ? 'Hotel' : 'Hotel')}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.hotel}
                  </td>
                </tr>
              )}
              {data.guidelang && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {isEn ? 'Guide language' : 'Reiseleitung'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {data.guidelang}
                  </td>
                </tr>
              )}
              {isBaustein && data.subtotal != null && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {isEn ? 'Subtotal' : 'Zwischensumme'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    {money(data.subtotal, !isEn)}
                  </td>
                </tr>
              )}
              {isBaustein && data.discount != null && data.discount > 0 && (
                <tr>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-foreground-500">
                    {isEn ? 'Discount' : 'Ermäßigung'}
                  </td>
                  <td className="py-2.5 px-1.5 border-b border-background-200 text-right font-semibold text-foreground-800">
                    −{money(data.discount, !isEn)}
                  </td>
                </tr>
              )}
              {data.total_price != null && (
                <tr className="bg-blue-50">
                  <td className="py-2.5 px-1.5 font-bold text-blue-900">
                    {isEn ? 'Total price' : 'Gesamtpreis'}
                  </td>
                  <td className="py-2.5 px-1.5 text-right font-bold text-blue-900">
                    {money(data.total_price, !isEn)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {isBaustein && <CheckInOutNote isDe={!isEn} className="mt-5" />}
        </div>

        {/* Footer */}
        <div className="vh-footer px-8 py-6 bg-blue-50 border-t border-blue-100 text-xs text-foreground-600 leading-relaxed">
          <p className="mb-4 text-blue-800 font-medium">
            {isEn
              ? 'All bookings on the website www.balivwtour.com are managed by our own registered travel company PT. Bali Chakra Tour.'
              : 'Alle Buchungen der Webseite www.balivwtour.com werden über unser eigenes registriertes Reiseunternehmen PT. Bali Chakra Tour verwaltet.'}
          </p>
          <strong className="text-blue-900">Bali Chakra Tours & Travel</strong>
          <br />
          Jalan Ganetri 9 D, Gatot Subroto Timur
          <br />
          Denpasar 80235, Bali, Indonesia
          <br />
          {isEn ? 'Phone' : 'Telefon'}: +62 361 8956561 · WhatsApp: +62 81 246 21 8000
          <br />
          {isEn ? 'Email' : 'E-Mail'}:{' '}
          <a
            href="mailto:office@balichakratours.com"
            className="text-blue-700 no-underline"
          >
            office@balichakratours.com
          </a>{' '}
          ·{' '}
          <a
            href="https://www.balichakratours.com"
            className="text-blue-700 no-underline"
          >
            www.balichakratours.com
          </a>
        </div>
      </div>
    </div>
  );
}